// Left rail for common filters plus the advanced nested AND/OR builder.
// Availability, labels, kinds, and enumerated values all come from the
// policy-reviewed effective field catalogue; catalogue failures stay visible.
import { useEffect, useMemo, useRef } from "react";
import { useExplorerStore } from "../store/explorerStore";
import { useCountries } from "../hooks/useCountries";
import { useFields } from "../hooks/useFields";
import { useCatalogueLabels } from "../hooks/useCatalogueLabels";
import { FacetCard } from "./FilterRail/FacetCard";
import { NumericRangeFacet } from "./FilterRail/NumericRangeFacet";
import { DateRangeFacet } from "./FilterRail/DateRangeFacet";
import { OwnT1dFacet, OWN_DX_FIELD, OWN_STAGE_FIELD } from "./FilterRail/OwnT1dFacet";
import {
  CHILD_AGE_FIELD,
  CHILD_DX_FIELD,
  CHILD_STAGE_FIELD,
  ChildrenT1dFacet,
  withoutChildConditions,
} from "./FilterRail/ChildrenT1dFacet";
import { fieldLabel, findTopLevelRule, setTopLevelRule } from "../lib/filterLogic";
import { flagFor } from "../lib/countryUtils";
import { CONTINENTS, continentOf, type Continent } from "../lib/continents";
import { useRailCollapse } from "../hooks/useRailCollapse";
import type { FilterRule } from "../types/inpact";

// Field paths verified 2026-05-05 against innodia-prod (see CLAUDE.md
// field-namespacing convention). Multipicklists (cert, PwD) get INCLUDES;
// single picklists (status, country, education) get IN.
const ASSOCIATE_STATUS_FIELD = "contact.INPACT_Associate_Status__c";
const COUNTRY_FIELD = "contact.Country_of_Residence_PL__c";
const COUNTRY_LEAD_FIELD = "contact.INPACT_Country_Lead__c";
const LEADING_TEAM_FIELD = "contact.INPACT_Leading_Team__c";
const CERT_FIELD = "cert.INPACT_Certification_Types__c";
const PWD_FIELD = "contact.PwD_Status__c";
const EDU_FIELD = "contact.Education__c";
const HAS_T1D_CHILD_FIELD = "derived.has_t1d_child";
const AGE_FIELD = "derived.age";
const CERT_COUNT_FIRST_LEVEL_FIELD = "derived.cert_count_first_level";
const CERT_COUNT_SECOND_LEVEL_FIELD = "derived.cert_count_second_level";
const CERT_COUNT_PROJECT_SPECIFIC_FIELD = "derived.cert_count_project_specific";
const APP_DATE_FIELD = "contact.Date_of_application__c";

const PWD_LABELS: Record<string, string> = {
  PwD: "Is a PwD",
  "Parent of a PwD": "Parent of a PwD",
  "Caregiver of a PwD": "Caregiver of a PwD",
};

type Tri = "yes" | "no" | "any";

function triFromRule(rule: FilterRule | null): Tri {
  if (!rule) return "any";
  if (rule.value === true) return "yes";
  if (rule.value === false) return "no";
  return "any";
}

function arrayValue(rule: FilterRule | null): string[] {
  if (!rule) return [];
  // The planner sometimes emits `=` / `IN` with a scalar (e.g. one country)
  // and sometimes with an array. The rail's checkbox state is identical in
  // both cases — wrap the scalar so the boxes light up either way.
  if (Array.isArray(rule.value)) return rule.value.map((v) => String(v).trim());
  if (typeof rule.value === "string") return [rule.value.trim()];
  if (typeof rule.value === "number") return [String(rule.value)];
  return [];
}


export function FilterRail() {
  const { filterGroup, setFilterGroup, resetFilters } = useExplorerStore();
  const advancedFilterOpen = useExplorerStore((s) => s.advancedFilterOpen);
  const openAdvancedFilter = useExplorerStore((s) => s.openAdvancedFilter);
  const { values: countryValues } = useCountries();
  const { fields, isLoading: fieldsLoading, error: fieldsError } = useFields();
  const fieldByKey = useMemo(
    () => new Map(fields.map((field) => [field.key, field])),
    [fields],
  );
  const canFilter = (key: string) => {
    const field = fieldByKey.get(key);
    return Boolean(field?.available && field.capabilities.includes("filter"));
  };
  const optionsFor = (key: string) => [...(fieldByKey.get(key)?.options ?? [])];
  const catalogueLabels = useCatalogueLabels();
  const { collapsed, toggle, isMobile } = useRailCollapse();

  const updateMulti = (field: string, value: string[]) => {
    if (value.length === 0) {
      setFilterGroup(setTopLevelRule(filterGroup, field, null));
    } else {
      // Multi-picklists (cert, PwD) need INCLUDES so the engine compares
      // against the parsed set; single picklists use IN against scalars.
      const op = fieldByKey.get(field)?.kind === "multipicklist" ? "INCLUDES" : "IN";
      setFilterGroup(
        setTopLevelRule(filterGroup, field, { field, op, value }),
      );
    }
  };

  const updateTri = (field: string, tri: Tri) => {
    if (tri === "any") {
      setFilterGroup(setTopLevelRule(filterGroup, field, null));
    } else {
      setFilterGroup(
        setTopLevelRule(filterGroup, field, {
          field,
          op: "=",
          value: tri === "yes",
        }),
      );
    }
  };

  const onHasChildChange = (v: Tri) => {
    let next =
      v === "any"
        ? setTopLevelRule(filterGroup, HAS_T1D_CHILD_FIELD, null)
        : setTopLevelRule(filterGroup, HAS_T1D_CHILD_FIELD, {
            field: HAS_T1D_CHILD_FIELD,
            op: "=",
            value: v === "yes",
          });
    if (v === "no") {
      // Has-child=No makes any child condition contradictory — clear every
      // child condition the facet governs (any operator, incl. legacy exact-age
      // leaves), so the filter can't silently return zero rows.
      next = withoutChildConditions(next);
    }
    setFilterGroup(next);
  };

  const ruleFor = (field: string) => findTopLevelRule(filterGroup, field);

  const status = arrayValue(ruleFor(ASSOCIATE_STATUS_FIELD));
  const certs = arrayValue(ruleFor(CERT_FIELD));
  const countries = arrayValue(ruleFor(COUNTRY_FIELD));
  // Group the country picklist by continent (display order = CONTINENTS),
  // alphabetical within each. Empty continents are dropped.
  const countryGroups = useMemo(() => {
    const byContinent = new Map<string, string[]>();
    for (const name of countryValues) {
      const key = continentOf(name);
      const bucket = byContinent.get(key);
      if (bucket) bucket.push(name);
      else byContinent.set(key, [name]);
    }
    return CONTINENTS.map((continent) => ({
      continent,
      items: (byContinent.get(continent.key) ?? [])
        .slice()
        .sort((a, b) => a.localeCompare(b)),
    })).filter((group) => group.items.length > 0);
  }, [countryValues]);
  const pwd = arrayValue(ruleFor(PWD_FIELD));
  const edu = arrayValue(ruleFor(EDU_FIELD));
  const lead = triFromRule(ruleFor(COUNTRY_LEAD_FIELD));
  const team = triFromRule(ruleFor(LEADING_TEAM_FIELD));
  const hasT1dChild = triFromRule(ruleFor(HAS_T1D_CHILD_FIELD));
  // When the user has explicitly set "Has child with T1D = No", the child
  // numeric-range facets cannot possibly match anything — grey them out so
  // staff don't waste time tuning min/max that the engine will short-circuit.
  const childFacetsDisabled = hasT1dChild === "no";
  const statusOptions = optionsFor(ASSOCIATE_STATUS_FIELD);
  const certOptions = optionsFor(CERT_FIELD);
  const pwdOptions = optionsFor(PWD_FIELD);
  const educationOptions = optionsFor(EDU_FIELD);
  const ownT1dAvailability = {
    stage: canFilter(OWN_STAGE_FIELD),
    diagnosisAge: canFilter(OWN_DX_FIELD),
  };
  const childT1dAvailability = {
    stage: canFilter(CHILD_STAGE_FIELD),
    age: canFilter(CHILD_AGE_FIELD),
    diagnosisAge: canFilter(CHILD_DX_FIELD),
  };
  const hasOwnT1dField = Object.values(ownT1dAvailability).some(Boolean);
  const hasChildT1dField = Object.values(childT1dAvailability).some(Boolean);
  const certificationCountFields = [
    CERT_COUNT_FIRST_LEVEL_FIELD,
    CERT_COUNT_SECOND_LEVEL_FIELD,
    CERT_COUNT_PROJECT_SPECIFIC_FIELD,
  ].filter(canFilter);

  const toggleIn = (field: string, current: string[], v: string) => {
    const next = current.includes(v) ? current.filter((x) => x !== v) : [...current, v];
    updateMulti(field, next);
  };

  // When collapsed, render only the toggle gutter — the rest of the rail is
  // visually and (for AT) functionally hidden. Mobile collapsed = backdrop-
  // overlay-when-expanded; desktop collapsed = thin gutter + main reflow.
  if (collapsed) {
    return (
      <aside className="filter-rail filter-rail--collapsed" aria-label="Filters (collapsed)">
        <button
          type="button"
          className="filter-rail__toggle filter-rail__toggle--collapsed"
          onClick={toggle}
          aria-expanded="false"
          aria-label="Expand filters"
          title="Expand filters"
        >❯</button>
      </aside>
    );
  }

  return (
    <>
      {isMobile && (
        <div className="filter-rail__backdrop" onClick={toggle} aria-hidden="true" />
      )}
      <aside className="filter-rail" aria-label="Filters">
      <div className="filter-rail__header">
        <span className="filter-rail__title">Filters</span>
        <div className="filter-rail__header-actions">
          <button
            type="button"
            className="filter-rail__reset"
            onClick={resetFilters}
          >
            Reset all
          </button>
          <button
            type="button"
            className="filter-rail__toggle"
            onClick={toggle}
            aria-expanded="true"
            aria-label="Collapse filters"
            title="Collapse filters"
          >❮</button>
        </div>
      </div>

      {fieldsLoading && <p role="status">Loading approved filter options…</p>}
      {fieldsError && (
        <p role="alert">Approved filter options could not be loaded. Retry shortly.</p>
      )}

      {/* Build advanced filter — TOP of the rail per Juan's correction. */}
      <button
        type="button"
        className="advanced-builder"
        onClick={() => openAdvancedFilter()}
        style={{ width: "100%", cursor: "pointer", display: "block" }}
        aria-haspopup="dialog"
        aria-expanded={advancedFilterOpen}
      >
        <strong>+ Build advanced filter</strong>
        <div style={{ marginTop: 4 }}>Nested AND/OR rules across all fields</div>
      </button>

      {canFilter(ASSOCIATE_STATUS_FIELD) && (
      <FacetCard title={fieldLabel(ASSOCIATE_STATUS_FIELD, catalogueLabels)} selectedCount={status.length} defaultOpen>
        <div className="pill-toggle" role="group" aria-label={fieldLabel(ASSOCIATE_STATUS_FIELD, catalogueLabels)}>
          {statusOptions.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status.includes(s)}
              onClick={() => toggleIn(ASSOCIATE_STATUS_FIELD, status, s)}
            >
              {s}
            </button>
          ))}
        </div>
      </FacetCard>
      )}

      {canFilter(CERT_FIELD) && (
      <FacetCard title={fieldLabel(CERT_FIELD, catalogueLabels)} selectedCount={certs.length} defaultOpen>
        <ul className="facet-checkbox-list">
          {certOptions.map((c) => (
            <li key={c}>
              <label>
                <input
                  type="checkbox"
                  checked={certs.includes(c)}
                  onChange={() => toggleIn(CERT_FIELD, certs, c)}
                />
                {c}
              </label>
            </li>
          ))}
        </ul>
      </FacetCard>
      )}

      {canFilter(COUNTRY_LEAD_FIELD) && (
      <FacetCard title={fieldLabel(COUNTRY_LEAD_FIELD, catalogueLabels)}>
        <TriToggle value={lead} onChange={(v) => updateTri(COUNTRY_LEAD_FIELD, v)} label={fieldLabel(COUNTRY_LEAD_FIELD, catalogueLabels)} />
      </FacetCard>
      )}

      {canFilter(LEADING_TEAM_FIELD) && (
      <FacetCard title={fieldLabel(LEADING_TEAM_FIELD, catalogueLabels)}>
        <TriToggle value={team} onChange={(v) => updateTri(LEADING_TEAM_FIELD, v)} label={fieldLabel(LEADING_TEAM_FIELD, catalogueLabels)} />
      </FacetCard>
      )}

      {canFilter(COUNTRY_FIELD) && (
      <FacetCard title={fieldLabel(COUNTRY_FIELD, catalogueLabels)} selectedCount={countries.length} defaultOpen>
        {fieldsLoading ? (
          <p style={{ color: "var(--color-neutral-400)", fontSize: "var(--font-size-xs)" }}>
            Loading countries…
          </p>
        ) : countryValues.length === 0 ? (
          <p role="alert" style={{ color: "var(--color-neutral-400)", fontSize: "var(--font-size-xs)" }}>
            Approved country options are unavailable.
          </p>
        ) : (
          <div className="continent-groups">
            {countryGroups.map(({ continent, items }) => (
              <ContinentGroup
                key={continent.key}
                continent={continent}
                countries={items}
                selected={countries}
                onChange={(next) => updateMulti(COUNTRY_FIELD, next)}
              />
            ))}
          </div>
        )}
      </FacetCard>
      )}

      {canFilter(PWD_FIELD) && (
      <FacetCard title={fieldLabel(PWD_FIELD, catalogueLabels)} selectedCount={pwd.length}>
        <ul className="facet-checkbox-list">
          {pwdOptions.map((value) => (
            <li key={value}>
              <label>
                <input
                  type="checkbox"
                  checked={pwd.includes(value)}
                  onChange={() => toggleIn(PWD_FIELD, pwd, value)}
                />
                {PWD_LABELS[value] ?? value}
              </label>
            </li>
          ))}
        </ul>
      </FacetCard>
      )}

      {hasOwnT1dField && (
      <FacetCard title="Their own T1D">
        <OwnT1dFacet availability={ownT1dAvailability} />
      </FacetCard>
      )}

      {canFilter(HAS_T1D_CHILD_FIELD) && (
      <FacetCard title={fieldLabel(HAS_T1D_CHILD_FIELD, catalogueLabels)}>
        <TriToggle
          value={hasT1dChild}
          onChange={onHasChildChange}
          label="Has child with T1D"
        />
      </FacetCard>
      )}

      {hasChildT1dField && (
      <FacetCard title="Their children's T1D">
        <ChildrenT1dFacet
          disabled={childFacetsDisabled}
          availability={childT1dAvailability}
        />
      </FacetCard>
      )}

      {certificationCountFields.length > 0 && (
      <FacetCard title="Certifications by level">
        <div className="facet-subgroup">
          {canFilter(CERT_COUNT_FIRST_LEVEL_FIELD) && (
          <>
          <div className="facet-subgroup__label">
            {fieldLabel(CERT_COUNT_FIRST_LEVEL_FIELD, catalogueLabels)}
          </div>
          <NumericRangeFacet
            field={CERT_COUNT_FIRST_LEVEL_FIELD}
            ariaLabel={fieldLabel(CERT_COUNT_FIRST_LEVEL_FIELD, catalogueLabels)}
          />
          </>
          )}
          {canFilter(CERT_COUNT_SECOND_LEVEL_FIELD) && (
          <>
          <div className="facet-subgroup__label">
            {fieldLabel(CERT_COUNT_SECOND_LEVEL_FIELD, catalogueLabels)}
          </div>
          <NumericRangeFacet
            field={CERT_COUNT_SECOND_LEVEL_FIELD}
            ariaLabel={fieldLabel(CERT_COUNT_SECOND_LEVEL_FIELD, catalogueLabels)}
          />
          </>
          )}
          {canFilter(CERT_COUNT_PROJECT_SPECIFIC_FIELD) && (
          <>
          <div className="facet-subgroup__label">
            {fieldLabel(CERT_COUNT_PROJECT_SPECIFIC_FIELD, catalogueLabels)}
          </div>
          <NumericRangeFacet
            field={CERT_COUNT_PROJECT_SPECIFIC_FIELD}
            ariaLabel={fieldLabel(CERT_COUNT_PROJECT_SPECIFIC_FIELD, catalogueLabels)}
          />
          </>
          )}
        </div>
      </FacetCard>
      )}

      {canFilter(APP_DATE_FIELD) && (
      <FacetCard title={fieldLabel(APP_DATE_FIELD, catalogueLabels)}>
        <DateRangeFacet field={APP_DATE_FIELD} ariaLabel={fieldLabel(APP_DATE_FIELD, catalogueLabels)} />
      </FacetCard>
      )}

      {canFilter(AGE_FIELD) && (
      <FacetCard title={fieldLabel(AGE_FIELD, catalogueLabels)}>
        <NumericRangeFacet field={AGE_FIELD} ariaLabel={fieldLabel(AGE_FIELD, catalogueLabels)} />
      </FacetCard>
      )}

      {canFilter(EDU_FIELD) && (
      <FacetCard title={fieldLabel(EDU_FIELD, catalogueLabels)} selectedCount={edu.length}>
        <ul className="facet-checkbox-list">
          {educationOptions.map((e) => (
            <li key={e}>
              <label>
                <input
                  type="checkbox"
                  checked={edu.includes(e)}
                  onChange={() => toggleIn(EDU_FIELD, edu, e)}
                />
                {e}
              </label>
            </li>
          ))}
        </ul>
      </FacetCard>
      )}
    </aside>
    </>
  );
}

// One collapsible continent group: a "select all" macro checkbox in the
// summary plus an alphabetical list of the countries present in that continent.
function ContinentGroup({
  continent,
  countries,
  selected,
  onChange,
}: {
  continent: Continent;
  countries: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const selectedSet = new Set(selected);
  const groupSelectedCount = countries.filter((c) => selectedSet.has(c)).length;

  const allSelected = countries.length > 0 && groupSelectedCount === countries.length;
  const indeterminate = groupSelectedCount > 0 && !allSelected;

  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  if (countries.length === 0) return null;

  const onToggleAll = () => {
    if (allSelected) {
      onChange(selected.filter((c) => !countries.includes(c)));
    } else {
      // Fill the rest (covers both "none" and "partial").
      onChange(Array.from(new Set([...selected, ...countries])));
    }
  };

  const onToggleOne = (name: string) => {
    onChange(
      selectedSet.has(name)
        ? selected.filter((c) => c !== name)
        : [...selected, name],
    );
  };

  return (
    <details className="continent-group" open>
      <summary className="continent-group__header">
        <input
          ref={ref}
          type="checkbox"
          className="continent-group__all"
          checked={allSelected}
          // Keep the checkbox toggling selection without collapsing the group.
          onClick={(e) => e.stopPropagation()}
          onChange={onToggleAll}
          aria-label={`Select all ${continent.label} — ${countries.length} countries`}
        />
        <span className="continent-group__label">
          <span aria-hidden="true">{continent.emoji}</span> {continent.label}
        </span>
        <span className="continent-group__hint">
          ({groupSelectedCount}/{countries.length})
        </span>
        <span className="continent-group__chevron" aria-hidden="true">›</span>
      </summary>
      <ul className="facet-checkbox-list">
        {countries.map((name) => (
          <li key={name}>
            <label>
              <input
                type="checkbox"
                checked={selectedSet.has(name)}
                onChange={() => onToggleOne(name)}
              />
              <span>{flagFor(name)} {name}</span>
            </label>
          </li>
        ))}
      </ul>
    </details>
  );
}

function TriToggle({
  value,
  onChange,
  label,
}: {
  value: Tri;
  onChange: (v: Tri) => void;
  label: string;
}) {
  return (
    <div className="tri-toggle" role="group" aria-label={label}>
      <button type="button" aria-pressed={value === "yes"} onClick={() => onChange("yes")}>Yes</button>
      <button type="button" aria-pressed={value === "no"} onClick={() => onChange("no")}>No</button>
      <button type="button" aria-pressed={value === "any"} onClick={() => onChange("any")}>Any</button>
    </div>
  );
}
